export type ServiceCategoryType = "CATEGORY" | "FORM" | "URL";

/** Раздел, в котором показывается категория. Пустое значение на бэке = `GENERAL`. */
export type ServiceCategorySection = "GENERAL" | "AGRO_CALCULATOR";

export const DEFAULT_SERVICE_CATEGORY_SECTION: ServiceCategorySection =
  "GENERAL";

export const SERVICE_CATEGORY_SECTION_OPTIONS: {
  value: ServiceCategorySection;
  label: string;
}[] = [
  { value: "GENERAL", label: "Основные" },
  { value: "AGRO_CALCULATOR", label: "Агрокалькулятор" },
];

export const isServiceCategorySection = (
  v: unknown,
): v is ServiceCategorySection => v === "GENERAL" || v === "AGRO_CALCULATOR";

/** Фильтр списка категорий услуг (`POST /service-category/get-all`). */
export type ServiceCategoryFilter = {
  page: number;
  size: number;
  type?: ServiceCategoryType;
  name?: string;
  deleted?: boolean;
  level?: number;
  ancestorId?: number | null;
  activeOnly?: boolean;
  /** Без раздела на level = 0 бэк отдаёт `GENERAL`, на остальных уровнях не фильтрует. */
  section?: ServiceCategorySection;
};

export type ServiceCategoryRequestDto = {
  id?: number;
  nameRu: string;
  nameKy: string;
  serviceCodes: string[];
  url?: string | null;
  isActive?: boolean;
  descriptionRu?: string | null;
  descriptionKy?: string | null;
  type: ServiceCategoryType;
  section: ServiceCategorySection;
  parentId?: number | null;
  /** Порядок отображения на странице (по возрастанию). */
  order?: number | null;

  deletedPhotoIds?: string[];
};

export type ServiceCategory = {
  id?: number;
  nameRu: string;
  nameKy: string;
  serviceCodes: string[];
  url?: string | null;
  isActive: boolean;
  descriptionRu?: string | null;
  descriptionKy?: string | null;
  photo: EntPhoto | null;
  type: ServiceCategoryType;
  section?: ServiceCategorySection | null;
  parentId?: number | null;
  /** Порядок отображения на странице (по возрастанию). */
  order?: number | null;
};

export class EntPhoto {
  id: string | null = null;
  fileName: string | null = null;
  filePathThumb: string | null = null;
  filePath: string | null = null;
  extension: string | null = null;
  type: string | null = null;
}

export const normalizeServiceCategory = (
  v: any,
  initialId?: number,
  deletedPhotoIds: string[] = [],
): ServiceCategoryRequestDto => ({
  id: initialId,
  nameRu: (v.nameRu ?? "").trim(),
  nameKy: (v.nameKy ?? "").trim(),
  serviceCodes: Array.from(new Set((v.serviceCodes ?? []).filter(Boolean))),
  url: v.url?.trim() ? v.url.trim() : null,
  isActive: !!v.isActive,
  descriptionRu: v.descriptionRu?.trim() || null,
  descriptionKy: v.descriptionKy?.trim() || null,
  type: v.type as ServiceCategoryType,
  section: isServiceCategorySection(v.section)
    ? v.section
    : DEFAULT_SERVICE_CATEGORY_SECTION,
  parentId: v.parentId ?? null,
  order:
    v.order === null || v.order === undefined || v.order === ""
      ? null
      : Number(v.order),
  deletedPhotoIds,
});

export const buildServiceCategoryFormData = (
  dto: ServiceCategoryRequestDto,
  files: File[] = [],
) => {
  const fd = new FormData();

  fd.append(
    "dto",
    new Blob([JSON.stringify(dto)], { type: "application/json" }),
  );

  for (const file of files) {
    fd.append("file", file);
  }

  return fd;
};

/**
 * Сравнение категорий по полю `order` (по возрастанию).
 * Записи без порядка уходят в конец, при равенстве — сортировка по названию.
 */
export const compareServiceCategoryOrder = (
  a: ServiceCategory,
  b: ServiceCategory,
) => {
  const ao = a.order ?? null;
  const bo = b.order ?? null;

  if (ao !== bo) {
    if (ao === null) return 1;
    if (bo === null) return -1;
    return ao - bo;
  }

  return (a.nameRu ?? "").localeCompare(b.nameRu ?? "", "ru");
};

/** Сортировка списка категорий по `order` без мутации исходного массива. */
export const sortServiceCategoriesByOrder = (list: ServiceCategory[]) =>
  [...(list ?? [])].sort(compareServiceCategoryOrder);
